import { Feature, FeatureCollection, Polygon} from "geojson";

let shapefileAvailable: boolean | null = null;

// Async function to generate statesData
// Helper function
function isJsonString(str: string): boolean {
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
}

// Function to check if shapefile is available (checked only once per session)
export async function checkShapefileAvailability(): Promise<boolean> {
    // Return cached value if already checked
    if (shapefileAvailable !== null) return shapefileAvailable;

    try {
        const response = await fetch(
            `/portal/api/filtered_org_strikes/${localStorage.getItem("selectedGroup")}`,
            {
                method: "GET",
                credentials: "same-origin",
            }
        );

        if (!response.ok) {
            console.warn("Shapefile check failed:", response.statusText);
            shapefileAvailable = false;
            return shapefileAvailable;
        }

        try {
            const data = await response.json();
            if (data && typeof data === "object" && "error" in data) {
                console.warn(data.error); // log the server-provided error only once
                shapefileAvailable = false;
                return shapefileAvailable;
            }
        } catch {
            // Parsing failed, assume okay (maybe binary response)
        }

        shapefileAvailable = true;
        return shapefileAvailable;

    } catch (error) {
        console.warn("Error checking shapefile availability:", error);
        shapefileAvailable = false;
        return shapefileAvailable;
    }
}

// New function to fetch pre-processed strike ranges from backend
export async function fetchPreprocessedOrgRanges(): Promise<Map<string, number> | null> {

    // Check availability only once per session
    if (shapefileAvailable === null) {
        shapefileAvailable = await checkShapefileAvailability();
    }

    // If we already know it's not available, skip the attempt
    if (!shapefileAvailable) {
        return null;
    }

    try {
        const resp = await fetch(`/portal/api/filtered_org_strikes/${localStorage.getItem("selectedGroup")}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
        });

        if (!resp.ok) {
            console.warn(`Strikes fetch failed: ${resp.statusText}`);
            return null;
        }

        let strikeRanges;
        try {
            strikeRanges = await resp.json();
        } catch (parseErr) {
            console.warn("Failed to parse JSON from strikes API:", parseErr);
            return null;
        }

        if (!strikeRanges || typeof strikeRanges !== "object") {
            console.warn("Unexpected format strikes API");
            return null;
        }

        return new Map(Object.entries(strikeRanges));
    } catch (err) {
        console.error("Error fetching strikes:", err);
        return null;
    }
}

export async function generateLightningData() {
    const filteredStrikesPerRange: Map<string, number> | null = await fetchPreprocessedOrgRanges();

    // If we got no data, return an empty FeatureCollection
    if (!filteredStrikesPerRange || filteredStrikesPerRange.size === 0) {
        console.warn("No lightning data available");
        return {
            type: "FeatureCollection",
            features: [],
        } as FeatureCollection<Polygon>;
    }

    const features: Feature<Polygon>[] = [];

    filteredStrikesPerRange.forEach((strikeCount, key) => {
        if (!isJsonString(key)) {
            console.warn("Failed to parse range key JSON. It came empty as there is no lightning data:", key);
            return; // continue
        }

        const coords: Array<[number, number]> = JSON.parse(key);

        features.push({
            type: "Feature",
            id: features.length.toString().padStart(2, "0"),
            properties: {
                name: `Strike Zone ${features.length + 1}`,
                density: strikeCount,
            },
            geometry: {
                type: "Polygon",
                coordinates: [coords],
            },
        });
    });


    const statesData: FeatureCollection = {
        type: "FeatureCollection",
        features,
    };

    return statesData;
}

export default generateLightningData;